// src/components/mdx/client/visualizations/blogChartLayout.ts

/**
 * Shared layout helpers for marketing data visualization blog charts.
 *
 * @module blogChartLayout
 */

import type { ReactNode } from 'react'

import { cn } from '~/lib/utils'

/**
 * Standard chart margins for blog visualizations.
 *
 * @param {boolean} isMobile - Whether the viewport is mobile width.
 * @param {Object} [overrides] - Optional margin overrides.
 * @returns {Object} Recharts margin object.
 */
export function getBlogChartMargin(
  isMobile: boolean,
  overrides?: Partial<{ top: number; right: number; left: number; bottom: number }>
): { top: number; right: number; left: number; bottom: number } {
  const base = isMobile
    ? { top: 10, right: 8, left: 0, bottom: 8 }
    : { top: 16, right: 16, left: 8, bottom: 16 }
  return { ...base, ...overrides }
}

/** Default throttle delay for Recharts pointer events on blog charts. */
export const BLOG_CHART_THROTTLE_MS = 50

/** Shared fill/font for Recharts bar-end and axis-adjacent labels. */
export const BLOG_CHART_LABEL_STYLE = {
  fill: 'rgb(209, 213, 219)',
  fontSize: 10
} as const

/**
 * Formats a numeric share/rate for horizontal bar end labels.
 *
 * @param {unknown} value - Raw Recharts label value.
 * @returns {string} Percentage label or empty string when invalid.
 */
export function formatBarEndPercent(value: unknown): string {
  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? `${numeric}%` : ''
}

/**
 * Formats a numeric currency metric for bar end labels.
 *
 * @param {unknown} value - Raw Recharts label value.
 * @returns {string} Currency label or empty string when invalid.
 */
export function formatBarEndCurrency(value: unknown): string {
  const numeric = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numeric)) {
    return ''
  }
  const decimals = numeric % 1 === 0 ? 0 : 1
  return `$${numeric.toFixed(decimals)}`
}

/**
 * Props for {@link BlogChartPlotArea}.
 *
 * @typedef {Object} BlogChartPlotAreaProps
 * @property {ReactNode} children - Chart content.
 * @property {string} [className] - Optional wrapper classes.
 */
export interface BlogChartPlotAreaProps {
  children: ReactNode
  className?: string
}

/**
 * Shared inset plot frame for blog charts — subtle border, radial glow, not bare canvas.
 *
 * @param {BlogChartPlotAreaProps} props - Plot area props.
 * @returns {JSX.Element} Styled chart container.
 */
export function BlogChartPlotArea({ children, className }: BlogChartPlotAreaProps): JSX.Element {
  return (
    <div
      className={cn(
        'relative h-full w-full overflow-hidden rounded-2xl border border-white/10',
        'bg-gradient-to-b from-white/[0.04] via-transparent to-black/20',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]',
        'before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_top,rgba(99,102,241,0.1),transparent_62%)]',
        className
      )}
    >
      <div className="relative h-full w-full p-2 sm:p-4">{children}</div>
    </div>
  )
}

/**
 * Props for the shared blog chart tooltip shell.
 *
 * @typedef {Object} BlogChartTooltipShellProps
 * @property {ReactNode} [title] - Optional tooltip title line.
 * @property {ReactNode} children - Tooltip body content.
 */
export interface BlogChartTooltipShellProps {
  title?: ReactNode
  children: ReactNode
}

/**
 * Renders the dark tooltip container shared across blog charts.
 *
 * @param {BlogChartTooltipShellProps} props - Shell props.
 * @returns {JSX.Element} Tooltip container.
 */
export function BlogChartTooltipShell({ title, children }: BlogChartTooltipShellProps): JSX.Element {
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900/95 p-3 text-sm shadow-xl backdrop-blur-sm">
      {title !== undefined && <p className="mb-1 font-medium text-gray-200">{title}</p>}
      {children}
    </div>
  )
}
